import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import { configureStore } from '@reduxjs/toolkit';
import localForage from 'localforage';
import { setupListeners } from '@reduxjs/toolkit/query';
import thunk from 'redux-thunk';
import {
  useDispatch as _useDispatch,
  useSelector as _useSelector,
  useStore as _useStore,
} from 'react-redux';

import type { TypedUseSelectorHook } from 'react-redux';

import rootReducer from 'reducers/index';

export type RootState = ReturnType<typeof rootReducer>;

export type AppStore = ReturnType<typeof createStore>['store'];
export type AppDispatch = AppStore['dispatch'];

const middlewares = [thunk];

const persistConfig = {
  key: 'root',
  storage: localForage,
  whitelist: ['company', 'user', 'settings', 'userLocation'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export function createStore(onComplete: () => void) {
  const store = configureStore({
    devTools: process.env.NODE_ENV !== 'production',
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(middlewares),
  });

  const persistor = persistStore(store, null, onComplete);

  setupListeners(store.dispatch);

  return { store, persistor };
}

// Use throughout your app instead of `useDispatch` and `useSelector` from 'react-redux'
export const useDispatch: () => AppDispatch = _useDispatch;
export const useSelector: TypedUseSelectorHook<RootState> = _useSelector;
export const useStore: () => AppStore = _useStore;
